import { useMemo } from "react";
import { useSelector } from "react-redux";
import { constants, utils } from "ethers";
import {
  NETWORKS,
  BLOCK_EXPLORERS,
  SUPPORTED_CHAINIDS_TYPE,
} from "../../constants/chains";
import { VaultDetails, selectVaultsList } from "../../store/vaults";
import { getColorByChainId } from "../../utils/getColorByChainId";
import { calculateVaultUSDBalance } from "../../utils/calculateVaultUSDBalance";
import moment from "moment";

const displayValue = (value: string) => utils.commify(Number(value).toFixed(0));

const VaultItemContent = ({
  vault,
  color,
  chainId,
}: {
  vault: VaultDetails;
  color: string;
  chainId: SUPPORTED_CHAINIDS_TYPE;
}) => {
  const usdBalance = calculateVaultUSDBalance(vault);
  const parsedBalance = utils.formatUnits(vault.balance ?? constants.Zero, 18);
  const parsedReserve0 = utils.formatUnits(
    vault.reserves0 ?? constants.Zero,
    vault.token0.decimals
  );
  const parsedReserve1 = utils.formatUnits(
    vault.reserves1 ?? constants.Zero,
    vault.token1.decimals
  );
  const parsedFeesEarned0 = utils.formatUnits(
    vault.feesEarned0 ?? constants.Zero,
    vault.token0.decimals.toString()
  );
  const parsedFeesEarned1 = utils.formatUnits(
    vault.feesEarned1 ?? constants.Zero,
    vault.token1.decimals.toString()
  );
  const { tokenPrice0, tokenPrice1 } = vault;
  const feesEarned = useMemo(() => {
    if (parsedFeesEarned0 && parsedFeesEarned1 && tokenPrice0 && tokenPrice1) {
      return (
        Number(parsedFeesEarned0) * tokenPrice0 +
        Number(parsedFeesEarned0) * tokenPrice1
      );
    } else return 0;
  }, [parsedFeesEarned0, parsedFeesEarned1, tokenPrice0, tokenPrice1]);
  const aprTimeago = moment.unix(vault.aprTimestamp).fromNow();
  return (
    <div className="vault-item">
      <div className="text-xl my-1 justify-center font-medium">
        <a
          className="px-[0.2em] ml-[-0.2em]"
          target="_blank"
          href={`https://beta.arrakis.finance/vaults/${chainId}/${vault.address}`}
        >
          {vault.name}
        </a>
      </div>
      <div
        className="vault-item__address"
        style={{ "--color": color } as React.CSSProperties}
      >
        {NETWORKS[chainId].name}{" "}
        <a
          className="px-[0.2em] mr-[-0.2em]"
          target="_blank"
          href={`${BLOCK_EXPLORERS[chainId].url}/address/${vault.address}`}
          title={BLOCK_EXPLORERS[chainId].name}
        >
          {vault.address}
        </a>
      </div>
      <div>
        <span>TVL:</span> ${vault.tvl.toLocaleString("en-US")}
      </div>
      <div>
        <span>Fee tier:</span> {vault ? vault.feeTier / 10000 : 0}%
      </div>
      <div>
        <span>Token prices:</span>
        <div className="flex flex-col items-end">
          <span>
            {vault.token0.symbol} ${tokenPrice0?.toLocaleString("en-US")}
          </span>
          <span>
            {vault.token1.symbol} ${tokenPrice1?.toLocaleString("en-US")}
          </span>
        </div>
      </div>
      <div>
        <span>Reserves:</span>
        <div className="flex flex-col items-end">
          <span>
            {displayValue(parsedReserve0)} {vault.token0.symbol}
          </span>
          <span>
            {displayValue(parsedReserve1)} {vault.token1.symbol}
          </span>
        </div>
      </div>
      <div>
        <span>Fees earned:</span>
        <div className="flex flex-col items-end">
          <span>
            {displayValue(parsedFeesEarned0)} {vault.token0.symbol}
          </span>
          <span>
            {displayValue(parsedFeesEarned1)} {vault.token1.symbol}
          </span>
        </div>
      </div>
      <div>
        <span>Lifetime Fees Earned:</span> ${feesEarned.toLocaleString("en-US")}
      </div>
      <div>
        <span>APR:</span> {vault.apr.toFixed(2)}%
      </div>
      <div>
        <span>Last calculated:</span> {aprTimeago.toLocaleLowerCase()}
      </div>
      <div>
        <span>Balance:</span> {parsedBalance.toString()} ${usdBalance}
      </div>
    </div>
  );
};

export const VaultItem = ({ vault }: { vault: VaultDetails }) => {
  const vaultsList = useSelector(selectVaultsList);
  const vaultInfo = vaultsList.find(
    (v) => utils.getAddress(v.address) === utils.getAddress(vault.address)
  );
  const color = getColorByChainId(vaultInfo?.chainId);
  return (
    <VaultItemContent
      vault={vault}
      color={color}
      chainId={vaultInfo?.chainId ?? 1}
    />
  );
};
